import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    getListOfAssignees, getListOfAssigneesFailure, getListOfAssigneesSuccess, getListOfShippingRequestsFailure, getListOfShippingRequestsSuccess, reassignBulkTickets, reassignBulkTicketsFailure, reassignBulkTicketsSuccess, setShippingRequestsFilter
} from 'src/app/compliance/store/actions/shipping-requests.actions';
import { selectFilter } from 'src/app/compliance/store/selectors/shipping-requests.selector';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { PagingToken } from 'src/app/shared/models/core';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { ShippingRequestsNSwagMigrationService } from '../../api/migration/shipping-requests-nswag-migration.service';
import { ShippingRequestGridItem } from '../../shipping-requests/shipping-request-grid-item';

@Injectable()
export class ShippingRequestsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly _shippingRequestsService: ShippingRequestsNSwagMigrationService
    ) {}

    getListOfAssignees$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getListOfAssignees),
            switchMap(() => {
                return this._shippingRequestsService.getAssignees().pipe(
                    map((res: Lookup[]) => getListOfAssigneesSuccess({ assignees: res })),
                    catchError((result) => of(getListOfAssigneesFailure({ error: result })))
                );
            })
        );
    });

    reassignBulkTickets$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(reassignBulkTickets),
            switchMap((action) => {
                return this._shippingRequestsService.reassignBulkTickets(action.assigneeId, action.ticketIds).pipe(
                    map((res: boolean) => reassignBulkTicketsSuccess({ success: res })),
                    catchError((result) => of(reassignBulkTicketsFailure({ error: result })))
                );
            })
        );
    });

    showReassignBulkTicketsMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(reassignBulkTicketsSuccess),
            map(() => showToasterMessage({ message: 'Successfully bulk reassign tickets.' }))
        );
    });

    getListOfShippingRequests$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setShippingRequestsFilter),
            concatLatestFrom(() => this._store.select(selectFilter)),
            switchMap(([action, filters]) => {
                return this._shippingRequestsService.searchShippingRequests(filters).pipe(
                    map((res: PagingToken<ShippingRequestGridItem[]>) =>
                        getListOfShippingRequestsSuccess({ shippingRequests: res })
                    ),
                    catchError((result) => of(getListOfShippingRequestsFailure({ error: result })))
                );
            })
        );
    });

    showGetListOfShippingRequestsFailureMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getListOfShippingRequestsFailure),
            map(() => showToasterMessage({ message: 'Failed to load shipping Requests.' }))
        );
    });
}
